import _ from 'lodash'
import {
  CONVERSION_FACTORS,
  METHANE_DENSITY_US,
  TIME_UNIT_LABELS,
  UNIT_TEMPLATES,
  convertMethaneDensityToUS,
} from '../constants/common'
import {
  MeasurementType,
  TimeUnit,
  UnitConfig,
  UnitSystem,
} from '../types/common'

type DisplayValue = {
  value: number
  unit: string
  label: string
}

export type FormattedEmissionValue = DisplayValue

/**
 * Validates the UnitConfig object and returns whether it's valid
 */
const isValidConfig = (config: UnitConfig): boolean => {
  if (!config || !config.system || !config.type) {
    return false
  }
  return (
    _.includes(_.values(UnitSystem), config.system) &&
    _.includes(_.values(MeasurementType), config.type) &&
    (config.timeUnit ? _.includes(_.values(TimeUnit), config.timeUnit) : true)
  )
}

type ConversionState = {
  value: number
  baseUnit: string
  unitPrefix: string
}

/**
 * Converts mass values to volume units using methane density
 */
const convertToVolumeUnits = (
  state: ConversionState,
  system: UnitSystem,
  methaneDensityKgM3?: number
): ConversionState => {
  // Use provided density or fall back to default (converted to US units)
  const methaneDensityUS = methaneDensityKgM3
    ? convertMethaneDensityToUS(methaneDensityKgM3)
    : METHANE_DENSITY_US

  // Convert mass to volume: kg → ft³
  const volumeValue = state.value / methaneDensityUS
  const volumeTemplate = UNIT_TEMPLATES[MeasurementType.VOLUME][system]

  if (system === UnitSystem.IMPERIAL) {
    return {
      // Convert to thousand cubic feet (mcf) for imperial volume
      value: volumeValue / 1000,
      unitPrefix: 'm',
      baseUnit: 'scf',
    }
  }

  return {
    // For metric volume, convert ft³ to m³
    value: volumeValue * CONVERSION_FACTORS.UNIT_SYSTEM.FT3_TO_M3,
    unitPrefix: '',
    baseUnit: volumeTemplate.unit,
  }
}

/**
 * Converts mass values to imperial mass units (pounds)
 */
const convertToImperialMass = (state: ConversionState): ConversionState => {
  const massTemplate = UNIT_TEMPLATES[MeasurementType.MASS][UnitSystem.IMPERIAL]
  return {
    value: state.value * CONVERSION_FACTORS.UNIT_SYSTEM.KG_TO_LB,
    unitPrefix: '',
    baseUnit: massTemplate.unit,
  }
}

/**
 * Keeps mass values in metric units (kilograms)
 */
const convertToMetricMass = (state: ConversionState): ConversionState => {
  const massTemplate = UNIT_TEMPLATES[MeasurementType.MASS][UnitSystem.METRIC]
  return {
    ...state,
    unitPrefix: '',
    baseUnit: massTemplate.unit,
  }
}

/**
 * Converts methane emission values between different units
 * Base assumption: input is always in kg/h (mass rate) or kg (mass total)
 *
 * @param valueMassKg - Base emission value in kg/h (rate) or kg (mass total)
 * @param convertedValueUnitConfig - Unit conversion configuration
 * @returns Converted value with appropriate unit (includes time unit unless config.isTotal is true)
 */
export const convertEmissionValue = (
  valueMassKg: number | null | undefined,
  convertedValueUnitConfig: UnitConfig
): {
  value?: number | null
  unit: string
} => {
  if (_.isNil(valueMassKg) || !isValidConfig(convertedValueUnitConfig)) {
    return { value: valueMassKg, unit: '' }
  }

  const {
    system,
    type,
    timeUnit = TimeUnit.HOUR,
    isTotal = false,
    methaneDensity,
  } = convertedValueUnitConfig
  const baseValue = _.toNumber(valueMassKg)

  const unitTemplate = UNIT_TEMPLATES[type][system]
  if (!unitTemplate) {
    return { value: valueMassKg, unit: '' }
  }

  const conversionState = {
    value: baseValue,
    baseUnit: unitTemplate.unit,
    unitPrefix: '',
  }

  const convertedState = _.cond([
    [
      () => type === MeasurementType.VOLUME,
      () => convertToVolumeUnits(conversionState, system, methaneDensity),
    ],
    [
      () => system === UnitSystem.IMPERIAL,
      () => convertToImperialMass(conversionState),
    ],
    [_.stubTrue, () => convertToMetricMass(conversionState)],
  ])()

  const finalValue =
    convertedState.value *
    (isTotal ? 1 : _.get(CONVERSION_FACTORS.TIME, timeUnit, 1))

  const unit = `${convertedState.unitPrefix}${convertedState.baseUnit}`
  const finalUnit = isTotal
    ? unit
    : `${unit}/${TIME_UNIT_LABELS[timeUnit]?.short}`

  return {
    value: finalValue,
    unit: finalUnit,
  }
}

/**
 * Converts emission values from any unit back to kg/h (base unit)
 * This is the reverse of convertEmissionValue
 *
 * @param value - Value in the current unit
 * @param currentUnitConfig - Current unit configuration
 * @returns Value converted to kg/h
 */
export const convertEmissionValueToBase = (
  value: number | null | undefined,
  currentUnitConfig: UnitConfig
): number | null => {
  if (_.isNil(value) || !isValidConfig(currentUnitConfig)) {
    return null
  }

  const {
    system,
    type,
    timeUnit = TimeUnit.HOUR,
    isTotal = false,
    methaneDensity,
  } = currentUnitConfig

  const methaneDensityUS = methaneDensity
    ? convertMethaneDensityToUS(methaneDensity)
    : METHANE_DENSITY_US

  let baseValue = _.toNumber(value)

  //  convert time unit back to per hour if not total
  // Reverse of: value * timeFactor, so we divide by timeFactor
  if (!isTotal) {
    baseValue /= _.get(CONVERSION_FACTORS.TIME, timeUnit, 1)
  }

  // Then convert unit type back to kg
  if (type === MeasurementType.VOLUME) {
    if (system === UnitSystem.IMPERIAL) {
      // Convert from mscf to ft³, then to kg
      // Reverse of: (kg / methaneDensityUS) / 1000
      const volumeFt3 = baseValue * 1000 // mscf to ft³
      baseValue = volumeFt3 * methaneDensityUS
    } else {
      // Convert from m³ to kg
      // Reverse of: (kg / methaneDensityUS) * FT3_TO_M3
      const volumeFt3 = baseValue / CONVERSION_FACTORS.UNIT_SYSTEM.FT3_TO_M3
      baseValue = volumeFt3 * methaneDensityUS
    }
  } else if (type === MeasurementType.MASS) {
    if (system === UnitSystem.IMPERIAL) {
      // Convert from pounds to kg
      // Reverse of: kg * KG_TO_LB, so we divide by KG_TO_LB
      baseValue /= CONVERSION_FACTORS.UNIT_SYSTEM.KG_TO_LB
    }
    // For metric mass, no conversion needed as it's already in kg
  }

  return baseValue
}
